import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowDownLeft, ArrowUpRight, Package, Search, Wrench } from 'lucide-react';
import type { Posicao } from '@wms/shared';
import { usePosicao, useTimeline } from '@/hooks/consultas';
import { useContexto } from '@/hooks/useContexto';
import { useSessao } from '@/hooks/useSessao';
import { normalizarCorredor } from '@/lib/layout-mapa';
import { formatarDimensoes, formatarPeso, formatarVolume, nomeExibido } from '@/lib/produto';
import { ESTILO_STATUS } from '@/lib/status';
import { dataHora, numero, plural } from '@/lib/utils';
import { Aba, Abas, ConteudoAba, ListaAbas } from '@/componentes/ui/abas';
import { Badge, Esqueleto, LinhasEsqueleto } from '@/componentes/ui/primitivos';
import { Botao } from '@/componentes/ui/botao';
import { EstadoErro, EstadoVazio } from '@/componentes/ui/estados';
import { Gaveta } from '@/componentes/ui/gaveta';
import { MedidaPosicao } from '@/componentes/admin/MedidaPosicao';
import { DrawerProduto } from '@/componentes/produto/DrawerProduto';
import { ImagemProduto } from '@/componentes/produto/ImagemProduto';

/**
 * Detalhe de posição. Único lugar do sistema que mostra o conteúdo de um
 * endereço — mapa, lista, busca e dashboard abrem todos esta mesma gaveta.
 */
export function DrawerPosicao({
  localizador,
  posicao,
  aoFechar,
}: {
  localizador: number | null;
  posicao?: Posicao | undefined;
  aoFechar: () => void;
}): JSX.Element {
  const { centro } = useContexto();
  const { temPermissao } = useSessao();
  const navegar = useNavigate();
  const [aba, definirAba] = React.useState('conteudo');
  const [fichaAberta, definirFichaAberta] = React.useState<number | null>(null);

  // Reabrir em outra posição volta para o conteúdo, não para a aba anterior.
  React.useEffect(() => {
    definirAba('conteudo');
    definirFichaAberta(null);
  }, [localizador]);

  const detalhe = usePosicao(centro, localizador);
  const podeVerHistorico = temPermissao('historico.ver');
  const timeline = useTimeline(centro, localizador, aba === 'historico' && podeVerHistorico);

  const estilo = posicao ? ESTILO_STATUS[posicao.status] : null;
  const titulo = posicao?.descricao || (localizador !== null ? `Posição ${localizador}` : '');

  return (
    <Gaveta
      aberta={localizador !== null}
      aoFechar={aoFechar}
      titulo={<span className="font-mono">{titulo}</span>}
      subtitulo={
        posicao
          ? `LOC ${posicao.localizador} · ${posicao.zona} · corredor ${
              posicao.corredor || '—'
            } · empresa ${posicao.empresa}`
          : `Localizador ${localizador ?? ''}`
      }
      acoes={
        posicao ? (
          <Botao
            variante="fantasma"
            tamanho="icone"
            aria-label="Ver este corredor no mapa"
            onClick={() => {
              navegar(`/estoque?centro=${centro ?? ''}&corredor=${encodeURIComponent(normalizarCorredor(posicao.corredor))}`);
              aoFechar();
            }}
          >
            <Search className="h-4 w-4" />
          </Botao>
        ) : null
      }
    >
      <div className="p-4">
        {estilo && posicao ? (
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <Badge className={estilo.classeBadge}>
              <span aria-hidden>{estilo.glifo}</span>
              {estilo.rotulo}
            </Badge>
            <Badge className="border-borda bg-superficie2 text-textoFraco">
              saldo <span className="tabular font-semibold text-texto">{numero(posicao.saldo)}</span>
            </Badge>
            <Badge className="border-borda bg-superficie2 text-textoFraco">
              {numero(posicao.skus)} {plural(posicao.skus, 'SKU', 'SKUs')}
            </Badge>
            {!posicao.ativo ? (
              <Badge className="border-borda bg-superficie2 text-textoFraco">inativa</Badge>
            ) : null}
          </div>
        ) : null}

        {/* Terceiro caminho até a tratativa (os outros dois são o alerta do
            Início e a aba em Movimentos): clicar na posição vermelha. */}
        {posicao && posicao.status === 'divergencia' ? (
          <div className="mb-4 rounded-lg border border-status-divergencia/40 bg-status-divergenciaFundo p-3">
            <p className="text-sm font-medium text-texto">Saldo negativo nesta posição</p>
            <p className="mb-2 text-xs text-textoFraco">
              O sistema deu baixa de algo que não estava aqui. Dá para compensar transferindo de
              outra posição que tenha o SKU.
            </p>
            <Botao
              variante="primaria"
              tamanho="pequeno"
              onClick={() => {
                navegar(`/movimentos/negativos?centro=${centro ?? ''}&negativo=${posicao.localizador}`);
                aoFechar();
              }}
            >
              <Wrench className="h-4 w-4" />
              Tratar negativo
            </Botao>
          </div>
        ) : null}

        <Abas value={aba} onValueChange={definirAba}>
          <ListaAbas>
            <Aba value="conteudo">Conteúdo</Aba>
            <Aba value="historico">Histórico</Aba>
            <Aba value="medida">Medida</Aba>
          </ListaAbas>

          <ConteudoAba value="conteudo">
            {detalhe.isLoading ? (
              <LinhasEsqueleto linhas={4} />
            ) : detalhe.isError ? (
              <EstadoErro erro={detalhe.error} aoTentarDeNovo={() => void detalhe.refetch()} />
            ) : (detalhe.data?.itens.length ?? 0) === 0 ? (
              <EstadoVazio
                titulo="Posição vazia"
                detalhe="Nenhum produto com saldo neste endereço."
                icone={<Package className="h-8 w-8" />}
              />
            ) : (
              <>
                <ul className="divide-y divide-borda">
                  {detalhe.data?.itens.map((item) => {
                    const dimensoes = formatarDimensoes(item.ficha);
                    return (
                      <li key={item.produto} className="flex items-start gap-3 py-3">
                        {/* A foto é o que o conferente confere primeiro: diz
                            se o item na prateleira é mesmo este. */}
                        <button
                          type="button"
                          onClick={() => definirFichaAberta(item.produto)}
                          aria-label={`Ver ficha do produto ${item.produto}`}
                        >
                          <ImagemProduto sku={item.produto} tamanho={56} alt={item.descricao} />
                        </button>

                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              className="numero-link font-mono text-sm font-semibold text-primaria"
                              onClick={() => {
                                navegar(`/estoque?centro=${centro ?? ''}&q=${item.produto}`);
                                aoFechar();
                              }}
                            >
                              {item.produto}
                            </button>
                            <button
                              type="button"
                              onClick={() => definirFichaAberta(item.produto)}
                              className="rounded text-xs text-textoFraco underline-offset-2 hover:underline"
                            >
                              ficha
                            </button>
                          </div>
                          <p className="truncate text-sm text-texto">
                            {nomeExibido(item.ficha, item.descricao)}
                          </p>
                          {item.ficha?.ean ? (
                            <p className="font-mono text-xs text-textoFraco">EAN {item.ficha.ean}</p>
                          ) : null}
                          <p className="mt-0.5 text-xs text-textoFraco">
                            {item.pesoTotalKg === null && item.volumeTotalM3 === null
                              ? 'sem peso/volume cadastrados'
                              : [
                                  item.pesoTotalKg === null ? null : formatarPeso(item.pesoTotalKg),
                                  item.volumeTotalM3 === null
                                    ? null
                                    : formatarVolume(item.volumeTotalM3),
                                ]
                                  .filter(Boolean)
                                  .join(' · ')}
                          </p>
                          {dimensoes ? (
                            <p className="text-xs text-textoFraco">unidade {dimensoes}</p>
                          ) : null}
                          <p className="mt-0.5 text-xs text-textoFraco">
                            entrada {dataHora(item.ultimaEntrada)}
                            {item.diasNaPosicao !== null
                              ? ` · ${numero(item.diasNaPosicao)} ${plural(item.diasNaPosicao, 'dia', 'dias')} na posição`
                              : ''}
                          </p>
                        </div>
                        <span className="tabular shrink-0 text-sm font-semibold">
                          {numero(item.saldo)}
                        </span>
                      </li>
                    );
                  })}
                </ul>

                <TotaisPosicao
                  peso={detalhe.data?.pesoTotalKg ?? null}
                  volume={detalhe.data?.volumeTotalM3 ?? null}
                />
              </>
            )}
          </ConteudoAba>

          <ConteudoAba value="historico">
            {!podeVerHistorico ? (
              <EstadoVazio
                titulo="Sem permissão para o histórico"
                detalhe="É preciso a permissão historico.ver para consultar a timeline da posição."
              />
            ) : timeline.isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 4 }, (_, i) => (
                  <Esqueleto key={i} className="h-12" />
                ))}
              </div>
            ) : timeline.isError ? (
              <EstadoErro erro={timeline.error} aoTentarDeNovo={() => void timeline.refetch()} />
            ) : (timeline.data?.eventos.length ?? 0) === 0 ? (
              <EstadoVazio
                titulo="Sem movimentações registradas"
                detalhe="Esta posição não tem eventos no período disponível."
              />
            ) : (
              <ol className="relative space-y-3 border-l border-borda pl-4">
                {timeline.data?.eventos.map((evento, indice) => {
                  const entrada = evento.entrada > 0;
                  return (
                    <li key={`${evento.dataHora}-${evento.produto}-${indice}`} className="relative">
                      <span
                        aria-hidden
                        className="absolute -left-[21px] top-1.5 flex h-3 w-3 items-center justify-center rounded-full border-2 border-superficie"
                        style={{
                          background: entrada
                            ? 'rgb(var(--st-livre))'
                            : 'rgb(var(--st-divergencia))',
                        }}
                      />
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-xs text-textoFraco">{dataHora(evento.dataHora)}</p>
                          <p className="truncate text-sm">
                            <span className="font-mono">{evento.produto}</span>{' '}
                            <span className="text-textoFraco">{evento.produtoDescricao}</span>
                          </p>
                          <p className="text-xs text-textoFraco">
                            mov. {evento.tipoMovimento}
                            {evento.documento !== null ? ` · doc. ${evento.documento}` : ''}
                          </p>
                        </div>
                        <div className="shrink-0 text-right">
                          <p
                            className="tabular flex items-center gap-1 text-sm font-semibold"
                            style={{
                              color: entrada ? 'rgb(var(--st-livre))' : 'rgb(var(--st-divergencia))',
                            }}
                          >
                            {entrada ? (
                              <ArrowDownLeft className="h-3.5 w-3.5" />
                            ) : (
                              <ArrowUpRight className="h-3.5 w-3.5" />
                            )}
                            {numero(entrada ? evento.entrada : evento.saida)}
                          </p>
                          <p className="tabular text-xs text-textoFraco">
                            saldo {numero(evento.saldoApos)}
                          </p>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </ConteudoAba>

          {/* Medida da vaga: a exceção ao padrão do CD, quando esta posição
              não segue o rack do resto. É o elo mais forte da cadeia
              localizador → centro → padrão. */}
          <ConteudoAba value="medida" className="px-4 pb-4">
            {localizador !== null ? <MedidaPosicao localizador={localizador} /> : null}
          </ConteudoAba>
        </Abas>
      </div>

      <DrawerProduto sku={fichaAberta} aoFechar={() => definirFichaAberta(null)} />
    </Gaveta>
  );
}

/**
 * Totais da posição. A API só soma quando TODOS os itens têm a medida;
 * quando vem `null`, dizemos por quê — um total parcial rotulado como total
 * levaria o conferente a fechar carga errada.
 */
function TotaisPosicao({
  peso,
  volume,
}: {
  peso: number | null;
  volume: number | null;
}): JSX.Element {
  return (
    <dl className="mt-3 grid grid-cols-2 gap-2 rounded-lg border border-borda bg-superficie2 p-3 text-sm">
      <div>
        <dt className="text-xs text-textoFraco">Peso total</dt>
        <dd className={peso === null ? 'text-xs text-textoFraco' : 'tabular font-semibold'}>
          {peso === null ? 'indisponível (SKU sem cadastro de peso)' : formatarPeso(peso)}
        </dd>
      </div>
      <div>
        <dt className="text-xs text-textoFraco">Volume total</dt>
        <dd className={volume === null ? 'text-xs text-textoFraco' : 'tabular font-semibold'}>
          {volume === null ? 'indisponível (SKU sem dimensões)' : formatarVolume(volume)}
        </dd>
      </div>
    </dl>
  );
}
